/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ShieldCheck, History, UserCog, Pencil, FileSearch, Database, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter34a — reason_code enum kept in sync with backend `_OWNERSHIP_REASON_CODES`
const REASON_CODES = [
  { value: "target_balancing", label: "Echilibrare target" },
  { value: "channel_deficit", label: "Deficit canal" },
  { value: "new_agent_no_sf_user", label: "Agent nou (fără user SF)" },
  { value: "dispute_resolution", label: "Rezolvare dispută" },
  { value: "manual_correction", label: "Corectare manuală" },
  { value: "initial_import", label: "Atribuire la import (inițial)" },
  { value: "reconciliation_match", label: "Match după reconciliere" },
];

const OWNERSHIP_SOURCE_LABEL = {
  manual_entry: "Introducere manuală",
  initial_easy: "import Easy",
  initial_easy_unmatched: "Easy (neasociat la import)",
  initial_legacy: "import CRM Legacy",
  initial_sf: "import Salesforce",
  manual_override: "override manual",
  reconciliation: "reconciliere",
};

/**
 * Iter31 P3b + Iter34a — Commercial Ownership panel for SaleDetail.
 *
 * Renders 3 ownership rows:
 *   1. Agent în sursă (raw): RAW name + code extracted from the import file
 *   2. Owner SF (informativ): from sf_agent_ref_id
 *   3. Owner comercial (autoritar): from commercial_agent_id (editable + auditable)
 * Plus reason_code enum, raw payload viewer, ownership provenance badge.
 *
 * Props:
 *   sale: full sale object (must include sf_agent_ref_id, commercial_agent_id,
 *         raw_agent_full_name, raw_agent_code, ownership_source_of_truth,
 *         ownership_override_flag, has_raw_payload)
 *   agents: cached list of all agents (id, full_name, primary_marca_code)
 *   currentUserRole: user.role from auth context
 *   onUpdate: callback after successful override (parent refetches sale)
 */
export default function CommercialOwnershipPanel({ sale, agents = [], currentUserRole, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rawPayloadOpen, setRawPayloadOpen] = useState(false);
  const [sourceDetailsOpen, setSourceDetailsOpen] = useState(false); // Iter35
  const [rawPayload, setRawPayload] = useState(null);
  const [rawPayloadLoading, setRawPayloadLoading] = useState(false);
  const [targetId, setTargetId] = useState(sale?.commercial_agent_id || "");
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [search, setSearch] = useState("");

  const canEdit = currentUserRole === "admin" || currentUserRole === "backoffice";

  useEffect(() => {
    setTargetId(sale?.commercial_agent_id || "");
    setReason("");
    setReasonCode("");
  }, [sale?.id, sale?.commercial_agent_id, open]);

  const agentMap = useMemo(() => {
    const m = new Map();
    (agents || []).forEach((a) => m.set(a.id, a));
    return m;
  }, [agents]);

  const sfAgent = sale?.sf_agent_ref_id ? agentMap.get(sale.sf_agent_ref_id) : null;
  const commAgent = sale?.commercial_agent_id ? agentMap.get(sale.commercial_agent_id) : null;

  const filteredAgents = useMemo(() => {
    if (!search.trim()) return agents.slice(0, 50);
    const q = search.trim().toLowerCase();
    return agents
      .filter((a) =>
        (a.full_name || "").toLowerCase().includes(q) ||
        (a.primary_marca_code || "").toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [agents, search]);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        commercial_agent_id: targetId || null,
        reason: reason.trim() || null,
        reason_code: reasonCode || null,
      };
      const res = await axios.put(`${API}/api/sales/${sale.id}/commercial-owner`, body);
      if (res.data?.no_change) {
        toast.info("Owner-ul comercial este deja setat la această valoare.");
      } else {
        toast.success("Owner comercial actualizat cu succes.");
        onUpdate && onUpdate();
      }
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare.");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/api/sales/${sale.id}/commercial-owner-history`);
      setHistory(res.data);
    } catch (e) {
      toast.error("Nu pot încărca istoricul.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setHistoryOpen(true);
    loadHistory();
  };

  const loadRawPayload = async () => {
    setRawPayloadLoading(true);
    try {
      const res = await axios.get(`${API}/api/sales/${sale.id}/raw-payload`);
      setRawPayload(res.data);
    } catch (e) {
      toast.error("Nu pot încărca raw payload.");
    } finally {
      setRawPayloadLoading(false);
    }
  };

  const openRawPayload = () => {
    setRawPayloadOpen(true);
    loadRawPayload();
  };

  const agentLabel = (a) => {
    if (!a) return "—";
    const code = a.primary_marca_code ? ` · ${a.primary_marca_code}` : "";
    return `${a.full_name || "Agent fără nume"}${code}`;
  };

  const rawSourceLabel = sale?.ownership_source_of_truth
    ? (OWNERSHIP_SOURCE_LABEL[sale.ownership_source_of_truth] || sale.ownership_source_of_truth)
    : null;

  return (
    <div
      className="col-span-2 rounded-lg border border-slate-200 bg-slate-50/50 p-4"
      data-testid="commercial-ownership-panel"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <ShieldCheck className="h-4 w-4 text-[#1e3a8a]" />
          Ownership comercial
          {rawSourceLabel && (
            <Badge variant="outline" className="ml-2 text-[10px]" data-testid="ownership-provenance-badge">
              sursă: {rawSourceLabel}
            </Badge>
          )}
          {sale?.ownership_override_flag && (
            <Badge className="ml-1 bg-amber-100 text-amber-900 text-[10px]" data-testid="ownership-override-badge">
              OVERRIDE
            </Badge>
          )}
          {sfAgent && commAgent && sfAgent.id !== commAgent.id && (
            <Badge
              className="ml-1 bg-rose-100 text-rose-900 text-[10px]"
              data-testid="ownership-mismatch-badge"
              title={`În SF apare ${agentLabel(sfAgent)}, dar semnatarul real este ${agentLabel(commAgent)}`}
            >
              SF ≠ REAL
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && sale?.has_raw_payload && (
            <Button
              variant="outline"
              size="sm"
              onClick={openRawPayload}
              className="h-7 px-2 text-xs"
              data-testid="raw-payload-btn"
            >
              <FileSearch className="mr-1 h-3 w-3" />
              Raw payload
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={openHistory}
            className="h-7 px-2 text-xs"
            data-testid="commercial-history-btn"
          >
            <History className="mr-1 h-3 w-3" />
            Istoric
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Iter34a — Agent în sursă (raw from import) */}
        <div data-testid="source-agent-display">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Agent în sursă (raw)
          </label>
          <div className="mt-1 text-sm text-slate-900">
            {sale?.raw_agent_full_name || sale?.raw_agent_code ? (
              <>
                {sale?.raw_agent_full_name || <em className="text-slate-400">fără nume</em>}
                {sale?.raw_agent_code && (
                  <span className="ml-1 font-mono text-xs text-slate-500">· {sale.raw_agent_code}</span>
                )}
                {/* Iter36 — match confidence + reconciliation status */}
                {typeof sale?.agent_match_confidence === "number" && sale.agent_match_confidence < 1.0 && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-[10px] py-0 px-1.5 border-amber-300 text-amber-700"
                    data-testid="agent-match-confidence-badge"
                    title="Scor fuzzy match agent (sub 1.0 = match parțial)"
                  >
                    match {Math.round(sale.agent_match_confidence * 100)}%
                  </Badge>
                )}
                {sale?.reconciliation_status && sale.reconciliation_status !== "ok" && (
                  <Badge
                    className="ml-1 bg-rose-100 text-rose-900 text-[10px] py-0 px-1.5"
                    data-testid="reconciliation-status-badge"
                    title="Status reconciliere"
                  >
                    {sale.reconciliation_status}
                  </Badge>
                )}
              </>
            ) : (
              <span className="italic text-slate-400">—</span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Brut din fișierul de import (Easy/Legacy/SF).
          </p>
        </div>

        {/* SF Owner — readonly */}
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Owner SF (informativ)
          </label>
          <div className="mt-1 flex items-center gap-2" data-testid="sf-owner-display">
            {sfAgent ? (
              <span className="text-sm text-slate-900">{agentLabel(sfAgent)}</span>
            ) : (
              <span className="text-sm italic text-slate-400">nesetat</span>
            )}
            <Badge variant="outline" className="ml-auto text-[10px] uppercase">readonly</Badge>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Atribuit de SF (nu poate fi editat manual)
          </p>
        </div>

        {/* Commercial Owner — editable */}
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Owner comercial (CRM — autoritar)
          </label>
          <div className="mt-1 flex items-center gap-2" data-testid="commercial-owner-display">
            {commAgent ? (
              <span className="text-sm font-medium text-slate-900">{agentLabel(commAgent)}</span>
            ) : (
              <span className="text-sm italic text-slate-400">nesetat</span>
            )}
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(true)}
                className="ml-auto h-7 px-2 text-[#1e3a8a]"
                data-testid="commercial-edit-btn"
              >
                <Pencil className="mr-1 h-3 w-3" />
                Modifică
              </Button>
            )}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Folosit pentru RBAC + rapoarte. Override-urile sunt auditate.
          </p>
        </div>
      </div>

      {/* Iter35 — Detalii sursă expandable: SF actor tracking + raw channel + role/partner */}
      {(sale?.source_channel_raw || sale?.source_partner_name || sale?.source_agent_role ||
        sale?.sf_started_by_name || sale?.sf_last_modified_by_name || sale?.sf_company_signatory_name) && (
        <div className="mt-3 rounded-md border border-slate-200 bg-white" data-testid="source-details-block">
          <button
            type="button"
            onClick={() => setSourceDetailsOpen(!sourceDetailsOpen)}
            className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-600 hover:bg-slate-50"
            data-testid="source-details-toggle"
          >
            <span className="flex items-center gap-2">
              <Database className="h-3.5 w-3.5" />
              Detalii sursă (informativ — audit/trasabilitate)
            </span>
            {sourceDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {sourceDetailsOpen && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-slate-100 px-3 py-3 text-xs md:grid-cols-3">
              {sale.source_channel_raw && (
                <div data-testid="source-channel-raw">
                  <label className="text-slate-500">Canal brut (SF/Easy)</label>
                  <p className="text-slate-900">{sale.source_channel_raw}</p>
                </div>
              )}
              {sale.source_partner_name && (
                <div data-testid="source-partner-name">
                  <label className="text-slate-500">Nume partener (Easy)</label>
                  <p className="text-slate-900">{sale.source_partner_name}</p>
                </div>
              )}
              {sale.source_agent_role && (
                <div data-testid="source-agent-role">
                  <label className="text-slate-500">Rol agent (Easy)</label>
                  <p className="text-slate-900">{sale.source_agent_role}</p>
                </div>
              )}
              {sale.sf_started_by_name && (
                <div data-testid="sf-started-by">
                  <label className="text-slate-500">Creat de (SF)</label>
                  <p className="text-slate-900">{sale.sf_started_by_name}</p>
                </div>
              )}
              {sale.sf_last_modified_by_name && (
                <div data-testid="sf-last-modified-by">
                  <label className="text-slate-500">Ultim modificator (SF)</label>
                  <p className="text-slate-900">{sale.sf_last_modified_by_name}</p>
                </div>
              )}
              {sale.sf_company_signatory_name && (
                <div data-testid="sf-company-signatory">
                  <label className="text-slate-500">Semnatar contract (SF)</label>
                  <p className="text-slate-900">{sale.sf_company_signatory_name}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Override dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="commercial-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-4 w-4 text-[#1e3a8a]" />
              Override Owner Comercial
            </DialogTitle>
            <DialogDescription>
              Modifică agentul comercial intern pentru această vânzare. Acțiunea
              este auditată cu cine, când, cod motiv și textul motiv opțional.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="agent-search" className="text-xs">Caută agent</Label>
              <Input
                id="agent-search"
                placeholder="Nume sau cod Marca…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mt-1"
                data-testid="agent-search-input"
              />
            </div>
            <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200">
              <button
                type="button"
                onClick={() => setTargetId("")}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                  targetId === "" ? "bg-amber-50 text-amber-900" : ""
                }`}
                data-testid="agent-option-none"
              >
                <span className="italic">— fără owner —</span>
              </button>
              {filteredAgents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setTargetId(a.id)}
                  className={`flex w-full items-center justify-between border-t border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                    targetId === a.id ? "bg-[#1e3a8a]/8 font-medium" : ""
                  }`}
                  data-testid={`agent-option-${a.id}`}
                >
                  <span>{agentLabel(a)}</span>
                  {a.id === sale?.sf_agent_ref_id && (
                    <Badge variant="outline" className="text-[10px]">= SF</Badge>
                  )}
                </button>
              ))}
              {filteredAgents.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-slate-400">
                  Niciun agent găsit.
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="reason-code" className="text-xs">Cod motiv (recomandat)</Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger id="reason-code" className="mt-1" data-testid="reason-code-select">
                  <SelectValue placeholder="Alege cod motiv…" />
                </SelectTrigger>
                <SelectContent>
                  {REASON_CODES.map((rc) => (
                    <SelectItem key={rc.value} value={rc.value} data-testid={`reason-code-${rc.value}`}>
                      {rc.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reason" className="text-xs">Motiv text (opțional)</Label>
              <Textarea
                id="reason"
                placeholder="Ex: corectare alocare după dispută BO…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 text-sm"
                rows={2}
                data-testid="reason-textarea"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Anulează
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90"
              data-testid="commercial-override-submit"
            >
              {saving ? "Se salvează…" : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="commercial-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-[#1e3a8a]" />
              Istoric override owner comercial
            </DialogTitle>
            <DialogDescription>
              Evenimente cronologice (cele mai recente primele).
            </DialogDescription>
          </DialogHeader>

          {historyLoading && (
            <div className="py-6 text-center text-sm text-slate-500">Se încarcă…</div>
          )}
          {!historyLoading && history && (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {history.history.length === 0 && (
                <div className="py-6 text-center text-sm italic text-slate-400">
                  Niciun override istoric. Owner-ul comercial provine din importul inițial.
                </div>
              )}
              {history.history.map((ev, i) => {
                const fromA = agentMap.get(ev.from_agent_id);
                const toA = agentMap.get(ev.to_agent_id);
                const rc = REASON_CODES.find((x) => x.value === ev.reason_code);
                return (
                  <div
                    key={i}
                    className="rounded-md border border-slate-200 bg-white p-3 text-sm"
                    data-testid={`history-entry-${i}`}
                  >
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{ev.timestamp ? new Date(ev.timestamp).toLocaleString("ro-RO") : "—"}</span>
                      <span className="font-mono">{ev.user_id?.slice(0, 8) || "—"}</span>
                    </div>
                    <div className="mt-1 text-slate-700">
                      <span className="line-through text-slate-400">
                        {fromA ? agentLabel(fromA) : <em>fără owner</em>}
                      </span>
                      <span className="mx-2 text-slate-400">→</span>
                      <span className="font-medium text-slate-900">
                        {toA ? agentLabel(toA) : <em>fără owner</em>}
                      </span>
                    </div>
                    {rc && (
                      <div className="mt-1">
                        <Badge variant="outline" className="text-[10px]">{rc.label}</Badge>
                      </div>
                    )}
                    {ev.reason && (
                      <div className="mt-1 italic text-xs text-slate-500">
                        „{ev.reason}"
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter34a — Raw payload dialog */}
      <Dialog open={rawPayloadOpen} onOpenChange={setRawPayloadOpen}>
        <DialogContent className="max-w-2xl" data-testid="raw-payload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-4 w-4 text-[#1e3a8a]" />
              Raw payload import
            </DialogTitle>
            <DialogDescription>
              Snapshot al rândului brut din fișierul de import (audit/debug).
            </DialogDescription>
          </DialogHeader>

          {rawPayloadLoading && (
            <div className="py-6 text-center text-sm text-slate-500">Se încarcă…</div>
          )}
          {!rawPayloadLoading && rawPayload && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-slate-500">Sursă:</span>{" "}
                  <span className="font-medium">{rawPayload.import_source || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Batch:</span>{" "}
                  <span className="font-mono">{rawPayload.import_batch_id?.slice(0, 8) || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Salvat:</span>{" "}
                  <span>{rawPayload.created_at ? new Date(rawPayload.created_at).toLocaleString("ro-RO") : "—"}</span>
                </div>
              </div>
              <pre
                className="max-h-96 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
                data-testid="raw-payload-json"
              >
                {rawPayload.raw_source_payload
                  ? JSON.stringify(rawPayload.raw_source_payload, null, 2)
                  : "— niciun payload salvat —"}
              </pre>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRawPayloadOpen(false)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
